import { differenceInCalendarDays } from "date-fns";

import { StaticData } from "../../../../core/flight-static-data";
import { ContentGroup } from "../../../../core/resource/types";

import { useLocalizedDateFormat } from "../../../../core";
import {
  FlightSearchAPIData,
  FlightSearchAPIDataWithSubclass,
  RefundableStatus,
  Segment,
} from "../../FlightSearch/types";
import createAirlines from "../../FlightSearch/utils/flight/createAirlines";
import createSeatClass from "../../FlightSearch/utils/flight/createSeatClass";
import flattenSegments from "../../FlightSearch/utils/flight/flattenSegments";
import parseFlightApiDate from "../../FlightSearch/utils/flight/parseFlightApiDate";
import { printDuration } from "../../FlightSearch/utils/flight/printDuration";
import {
  FlightSummaryComponentProps,
  JourneyDataLegacy,
  SummaryDataLegacy,
} from "../FlightSummary";

export function getSummaryFromApiSearchData(
  journey: FlightSearchAPIData["journeys"],
  staticData: StaticData,
  cr: ContentGroup,
  formatter: ReturnType<typeof useLocalizedDateFormat>["format"]
): FlightSummaryComponentProps["summary"] {
  const { airportMap } = staticData;
  const segments = flattenSegments(journey);

  const airlines = createAirlines(segments, staticData, cr);
  const departureSegment = segments[0];
  const arrivalSegment = segments[segments.length - 1];

  const departureAirport =
    airportMap[departureSegment.departureDetail.airportCode];
  const arrivalAirport = airportMap[arrivalSegment.arrivalDetail.airportCode];

  const departureDateTime = parseFlightApiDate(
    departureSegment.departureDetail.departureDate,
    departureSegment.departureDetail.departureTime
  );
  const arrivalDateTime = parseFlightApiDate(
    arrivalSegment.arrivalDetail.arrivalDate,
    arrivalSegment.arrivalDetail.arrivalTime
  );

  const { isMultiClass, seatClasses } = createSeatClass(segments);

  const duration = journey.reduce(
    (acc, cur) => acc + Number(cur.journeyDuration),
    0
  );

  const airlineNames = Array.from(
    new Set(
      airlines
        .map((a) => a.name)
        .filter((n): n is string => typeof n === "string")
    )
  );

  return {
    airlineName: airlineNames.length > 1 ? cr.multiAirline : airlineNames[0],
    airlineLogos: airlines.map((a) => a.image),
    departureDetail: {
      departureDate: formatter(departureDateTime, "SHORT_WEEKDAY_2"),
      departureTime: departureSegment.departureDetail.departureTime,
      departureTerminal: departureSegment.departureDetail.departureTerminal,
      airport: {
        city: departureAirport?.city || "",
        airportCode: departureAirport?.airportCode || "",
        internationalAirportName:
          departureAirport?.internationalAirportName || "",
      },
    },
    arrivalDetail: {
      arrivalDate: formatter(arrivalDateTime, "SHORT_WEEKDAY_2"),
      arrivalTime: arrivalSegment.arrivalDetail.arrivalTime,
      arrivalTerminal: arrivalSegment.arrivalDetail.arrivalTerminal,
      airport: {
        city: arrivalAirport?.city || "",
        airportCode: arrivalAirport?.airportCode || "",
        internationalAirportName:
          arrivalAirport?.internationalAirportName || "",
      },
    },
    duration: printDuration(duration, cr),
    dateOffset: differenceInCalendarDays(arrivalDateTime, departureDateTime),
    numOfTransit:
      segments.length + segments.filter((s) => s.stopInfo).length - 1,
    seatClass: isMultiClass ? cr.MIXED : cr[seatClasses[0]],
  };
}

/**
 *
 * @param journey
 * @param staticData
 * @param cr CorporateEnumSeatClass, CorporateFlightGeneral
 * @param formatter
 * @returns
 */
export function getTripFromApiSearchData(
  journey:
    | FlightSearchAPIData["journeys"]
    | FlightSearchAPIDataWithSubclass["journeys"],
  staticData: StaticData,
  cr: ContentGroup,
  formatter: ReturnType<typeof useLocalizedDateFormat>["format"]
): FlightSummaryComponentProps["trip"] {
  const { airportMap } = staticData;
  const segments = flattenSegments(journey);

  const departureSegment = segments[0];
  const arrivalSegment = segments[segments.length - 1];

  const departureAirport =
    airportMap[departureSegment.departureDetail.airportCode];
  const arrivalAirport = airportMap[arrivalSegment.arrivalDetail.airportCode];

  const duration = journey.reduce(
    (acc, cur) => acc + Number(cur.journeyDuration),
    0
  );

  return {
    departureAirportCity: departureAirport?.city || "",
    arrivalAirportCity: arrivalAirport?.city || "",
    totalDuration: printDuration(duration, cr),
    journeys: journey.map((j, jIndex, jArr) => {
      const airlines = createAirlines(j.segments, staticData, cr);
      const { isMultiClass, seatClasses } = createSeatClass(j.segments);

      const departureAirport = airportMap[j.departureDetail.airportCode];
      const arrivalAirport = airportMap[j.arrivalDetail.airportCode];

      const airlineNames = Array.from(
        new Set(
          airlines
            .map((a) => a.name)
            .filter((n): n is string => typeof n === "string")
        )
      );
      return {
        arrivalAirportCity: arrivalAirport?.city || "",
        departureAirportCity: departureAirport?.city || "",
        seatClass: isMultiClass ? cr.MIXED : cr[seatClasses[0]],
        airlineNames:
          airlineNames.length > 1 ? cr.multiAirline : airlineNames[0],
        airlineLogos: airlines.map((a) => a.image),
        refundableStatus: j.refundableStatus,
        segments: j.segments.map((segment, sIndex, sArr) => {
          const depAirport = airportMap[segment.departureDetail.airportCode];
          const arrAirport = airportMap[segment.arrivalDetail.airportCode];
          const airline = createAirlines([segment], staticData, cr)[0];

          let prevSegment: Nullable<Segment> = null;
          if (sIndex > 0) {
            prevSegment = sArr[sIndex - 1];
          } else if (sIndex === 0 && jIndex > 0) {
            const prevJourney = jArr[jIndex - 1];
            prevSegment = prevJourney.segments[prevJourney.segments.length - 1];
          }

          return {
            airlines: airline,
            arrivalDetail: {
              arrivalDate: formatter(
                new Date(segment.arrivalDetail.arrivalDate),
                "SHORT_MONTH"
              ),
              arrivalTime: segment.arrivalDetail.arrivalTime,
              arrivalTerminal: segment.arrivalDetail.arrivalTerminal,
              airport: {
                airportCode: segment.arrivalDetail.airportCode,
                city: arrAirport?.city || "",
                internationalAirportName:
                  arrAirport?.internationalAirportName || "",
              },
            },
            departureDetail: {
              departureDate: formatter(
                new Date(segment.departureDetail.departureDate),
                "SHORT_MONTH"
              ),
              departureTime: segment.departureDetail.departureTime,
              departureTerminal: segment.departureDetail.departureTerminal,
              airport: {
                airportCode: segment.departureDetail.airportCode,
                city: depAirport?.city || "",
                internationalAirportName:
                  depAirport?.internationalAirportName || "",
              },
            },
            duration: printDuration(
              parseInt(segment.flightDurationInMinutes),
              cr
            ),
            seatClass: cr[segment.seatClass],
            transitInfo:
              segment.transitDurationInMinutes === null ||
              segment.transitDurationInMinutes === "0"
                ? null
                : {
                    duration: printDuration(
                      parseInt(segment.transitDurationInMinutes),
                      cr
                    ),
                    airport: {
                      city: depAirport?.city || "",
                      airportCode: depAirport?.airportCode || "",
                      internationalAirportName:
                        depAirport?.internationalAirportName || "",
                    },
                    changeAirport:
                      prevSegment?.arrivalDetail.airportCode !==
                      segment.departureDetail.airportCode,
                    visaRequired: segment.visaRequired,
                    mayNeedReCheckIn: segment.mayNeedReCheckIn,
                  },
            stopInfo: segment.stopInfo
              ? {
                  internationalAirportName:
                    airportMap[segment.stopInfo.airportCode]
                      ?.internationalAirportName || "",
                  duration: printDuration(
                    parseInt(segment.stopInfo.durationMinutes),
                    cr
                  ),
                }
              : null,
            subclass:
              ("tier" in segment && segment.tier?.subclass) || undefined,
            fareName:
              ("tier" in segment && segment.tier?.tierName) || undefined,
          };
        }),
      };
    }),
  };
}

export function getSummaryFromPrebookStorage(
  summary: SummaryDataLegacy,
  journeys: JourneyDataLegacy[],
  staticData: StaticData,
  cr: ContentGroup,
  formatter: ReturnType<typeof useLocalizedDateFormat>["format"]
): FlightSummaryComponentProps["summary"] {
  const { airlineMap } = staticData;
  const airlineCodes = Array.from(
    new Set(journeys.flatMap((j) => j.segments.map((s) => s.airlineCode)))
  );
  const singleAirlineCode = airlineCodes[0];
  return {
    airlineName:
      airlineCodes.length > 1
        ? cr.multiAirline
        : airlineMap[singleAirlineCode]?.airlineName || singleAirlineCode,
    duration: printDuration(summary.duration, cr),
    seatClass: summary.seatClass,
    airlineLogos: summary.airlineLogos,
    departureDetail: {
      airport: {
        airportCode: summary.departureAirport.airportCode,
        city: summary.departureAirport.city,
        internationalAirportName:
          summary.departureAirport.internationalAirportName,
      },
      departureDate: formatter(
        new Date(summary.departureDate),
        "SHORT_WEEKDAY_2"
      ),
      departureTime: summary.departureTime,
      departureTerminal: summary.departureTerminal,
    },
    arrivalDetail: {
      airport: {
        airportCode: summary.arrivalAirport.airportCode,
        city: summary.arrivalAirport.city,
        internationalAirportName:
          summary.arrivalAirport.internationalAirportName,
      },
      arrivalDate: formatter(new Date(summary.arrivalDate), "SHORT_WEEKDAY_2"),
      arrivalTime: summary.arrivalTime,
      arrivalTerminal: summary.arrivalTerminal,
    },
    dateOffset: summary.offset,
    numOfTransit: summary.transit,
  };
}

export function getTripFromPrebookStorage(
  journeys: JourneyDataLegacy[],
  staticData: StaticData,
  cr: ContentGroup,
  formatter: ReturnType<typeof useLocalizedDateFormat>["format"]
): FlightSummaryComponentProps["trip"] {
  const { airportMap, airlineMap } = staticData;
  const segments = journeys.flatMap((j) => j.segments);
  const departureSegment = segments[0];
  const arrivalSegment = segments[segments.length - 1];

  const totalDuration = segments.reduce(
    (acc, curr) => acc + curr.duration + (curr.transit?.duration || 0),
    0
  );

  return {
    departureAirportCity:
      departureSegment.departureAirport?.city ||
      departureSegment.departureAirportName,
    arrivalAirportCity:
      arrivalSegment.arrivalAirport?.city ||
      departureSegment.departureAirportName,
    totalDuration: printDuration(totalDuration, cr),
    journeys: journeys.map((j) => {
      const journeyDepartureSegment = j.segments[0];
      const journeyArrivalSegment = j.segments[j.segments.length - 1];
      const airlineCodes = Array.from(
        new Set(j.segments.map((s) => s.airlineCode))
      );
      const singleAirlineCode = airlineCodes[0];
      return {
        arrivalAirportCity:
          journeyDepartureSegment.departureAirport?.city ||
          journeyDepartureSegment.departureAirportName,
        departureAirportCity:
          journeyArrivalSegment.arrivalAirport?.city ||
          journeyArrivalSegment.arrivalAirportName,
        seatClass: j.seatClass,
        airlineNames:
          airlineCodes.length > 1
            ? cr.multiAirline
            : airlineMap[singleAirlineCode]?.airlineName || singleAirlineCode,
        airlineLogos: j.airlineLogos,
        refundableStatus: j.refund?.isAvailable
          ? RefundableStatus.REFUNDABLE
          : RefundableStatus.NON_REFUNDABLE,
        segments: j.segments.map((s) => ({
          departureDetail: {
            airport: {
              city: s.departureAirport?.city || s.departureAirportName,
              airportCode:
                s.departureAirport?.airportCode || s.departureAirportName,
              internationalAirportName: s.departureAirportName,
            },
            departureDate: formatter(new Date(s.departureDate), "SHORT_MONTH"),
            departureTime: s.departureTime,
            departureTerminal: s.departureTerminal,
          },
          arrivalDetail: {
            airport: {
              city: s.arrivalAirport?.city || s.arrivalAirportName,
              airportCode:
                s.arrivalAirport?.airportCode || s.arrivalAirportName,
              internationalAirportName:
                s.arrivalAirport?.internationalAirportName ||
                s.arrivalAirportName,
            },
            arrivalDate: formatter(new Date(s.arrivalDate), "SHORT_MONTH"),
            arrivalTime: s.arrivalTime,
            arrivalTerminal: s.arrivalTerminal,
          },
          duration: printDuration(s.duration, cr),
          airlines: {
            name: airlineMap[s.airlineCode]?.airlineName || s.airlineCode,
            code: s.airlineCode,
            flightCode: s.airlineCode,
            image: s.airlineLogo,
            facilities: s.facilities,
            hasBaggage: s.facilities.some((f) => f.type === "baggage"),
            hasInFlightMeal: s.facilities.some((f) => f.type === "meal"),
          },
          seatClass: s.seatClass,
          stopInfo: s.stopOver
            ? {
                duration: printDuration(s.stopOver.duration, cr),
                internationalAirportName:
                  airportMap[s.stopOver.airportCode]
                    ?.internationalAirportName || s.stopOver.airportCode,
              }
            : null,
          transitInfo: s.transit
            ? {
                duration: printDuration(s.transit.duration, cr),
                airport: {
                  city:
                    airportMap[s.transit.airportCode]?.city ||
                    s.transit.airportCode,
                  airportCode: s.transit.airportCode,
                  internationalAirportName:
                    airportMap[s.transit.airportCode]
                      ?.internationalAirportName || s.transit.airportCode,
                },
                visaRequired: s.visaRequired,
                mayNeedReCheckIn: s.mayNeedReCheckIn,
              }
            : null,
        })),
      };
    }),
  };
}
